"use client";

import { useMemo, useState, useTransition } from "react";
import { saveCompanionFeedback } from "@/app/actions/companion-feedback";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import type { CompanionFeedbackKind } from "@/lib/companion/feedback";
import type { GroundTruthSport } from "@/lib/guardian/ground-truth-reports";
import type {
  GroundTruthReviewWindow,
  GroundTruthSummary,
} from "@/lib/conditions/ground-truth-summary";
import { sportIsBeta, sportLabel } from "@/lib/conditions/sport-meta";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { SportBetaBadge } from "./SportBetaBadge";

type GroundTruthReviewProps = {
  locationSlug: string;
  locationName: string;
  sport: string;
  windows: GroundTruthReviewWindow[];
  summary: GroundTruthSummary;
};

type Status =
  | { kind: "idle"; message: string }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

const FEEDBACK_OPTIONS: { kind: CompanionFeedbackKind; label: string }[] = [
  { kind: "accurate", label: "Accurate" },
  { kind: "better-than-forecast", label: "Better" },
  { kind: "worse-than-forecast", label: "Worse" },
  { kind: "wrong", label: "Wrong" },
  { kind: "icy", label: "Icy" },
  { kind: "poor", label: "Poor" },
];

const SCORE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function GroundTruthReview({
  locationSlug,
  locationName,
  sport,
  windows,
  summary,
}: GroundTruthReviewProps) {
  const { isLoggedIn } = useSpotUser();
  const [selectedWindowId, setSelectedWindowId] = useState(windows[0]?.id ?? "");
  const [observedScore, setObservedScore] = useState<number | null>(null);
  const [kind, setKind] = useState<CompanionFeedbackKind>("accurate");
  const [status, setStatus] = useState<Status>({
    kind: "idle",
    message: "Score this window against what you actually found.",
  });
  const [pending, startTransition] = useTransition();

  const selectedWindow = useMemo(
    () => windows.find((window) => window.id === selectedWindowId) ?? windows[0] ?? null,
    [selectedWindowId, windows],
  );
  const canSubmit = Boolean(selectedWindow && observedScore !== null && !pending);

  return (
    <div className="space-y-4">
      <section
        aria-labelledby={`ground-truth-${sport}`}
        className="rounded-[16px] border border-border-light bg-white p-5 md:p-6"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3
              id={`ground-truth-${sport}`}
              className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted"
            >
              Review conditions
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[13px] leading-relaxed text-text-muted">
              <p>{sportLabel(sport)} at {locationName}</p>
              {sportIsBeta(sport) ? <SportBetaBadge /> : null}
            </div>
          </div>
          {selectedWindow ? (
            <span className="rounded-full bg-navy/[0.06] px-3 py-1 text-[12px] font-semibold text-navy">
              PeakWave {selectedWindow.predictedLabel}
            </span>
          ) : null}
        </div>

        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="mb-2 block text-[12px] font-semibold text-text-muted">
              Window reviewed
            </span>
            <select
              value={selectedWindowId}
              onChange={(event) => setSelectedWindowId(event.target.value)}
              disabled={pending || windows.length === 0}
              className="min-h-[44px] w-full rounded-sm border border-border bg-warm-bg px-3 text-[14px] font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-gold/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {windows.map((window) => (
                <option key={window.id} value={window.id}>
                  {window.label}
                </option>
              ))}
            </select>
          </label>

          <div>
            <p className="mb-2 text-[12px] font-semibold text-text-muted">
              Your score
            </p>
            <div className="grid grid-cols-5 gap-2">
              {SCORE_OPTIONS.map((score) => (
                <button
                  key={score}
                  type="button"
                  aria-pressed={observedScore === score}
                  disabled={pending}
                  onClick={() => setObservedScore(score)}
                  className={`inline-flex min-h-[44px] items-center justify-center rounded-sm border px-2 py-2 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-55 ${
                    observedScore === score
                      ? "border-gold bg-gold text-navy"
                      : "border-border bg-warm-bg text-navy hover:border-gold/50 hover:bg-white"
                  }`}
                >
                  {score}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-[12px] font-semibold text-text-muted">
              Forecast feel
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {FEEDBACK_OPTIONS.map((option) => (
                <button
                  key={option.kind}
                  type="button"
                  aria-pressed={kind === option.kind}
                  disabled={pending}
                  onClick={() => setKind(option.kind)}
                  className={`inline-flex min-h-[44px] items-center justify-center rounded-sm border px-3 py-2 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-55 ${
                    kind === option.kind
                      ? "border-navy bg-navy text-white"
                      : "border-border bg-warm-bg text-navy hover:border-gold/50 hover:bg-white"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p
              role={status.kind === "idle" ? undefined : "status"}
              className={`text-[12.5px] leading-relaxed ${
                status.kind === "error"
                  ? "text-rose-700"
                  : status.kind === "success"
                    ? "text-emerald-700"
                    : "text-text-muted"
              }`}
            >
              {status.message}
            </p>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => {
                if (!isLoggedIn) {
                  setStatus({ kind: "error", message: "Log in to review conditions." });
                  return;
                }
                if (!selectedWindow || observedScore === null) return;
                startTransition(async () => {
                  const result = await saveCompanionFeedback({
                    locationSlug,
                    sport: sport as GroundTruthSport,
                    kind,
                    predictedScore: selectedWindow.predictedScore,
                    predictedLabel: selectedWindow.predictedLabel,
                    observedScore,
                    reviewedStartAt: selectedWindow.reviewedStartAt,
                    reviewedEndAt: selectedWindow.reviewedEndAt,
                  });
                  if (result.ok || ("reportSaved" in result && result.reportSaved)) {
                    setObservedScore(null);
                  }
                  setStatus(
                    result.ok
                      ? { kind: "success", message: "Review saved. It will feed the local accuracy score." }
                      : { kind: "error", message: result.error },
                  );
                });
              }}
              className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-sm bg-navy px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pending ? "Saving" : "Save review"}
            </button>
          </div>
        </div>
      </section>

      <GroundTruthSummaryCard summary={summary} sport={sport} />
    </div>
  );
}

function GroundTruthSummaryCard({
  summary,
  sport,
}: {
  summary: GroundTruthSummary;
  sport: string;
}) {
  if (summary.totalReports === 0) {
    return (
      <section className="rounded-[16px] border border-border-light bg-white p-5 md:p-6">
        <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
          Local scores today
        </h3>
        <p className="mt-2 text-[13px] leading-relaxed text-text-muted">
          No scored {sportName(sport).toLowerCase()} reviews in the last 24 hours.
        </p>
      </section>
    );
  }

  const agrees =
    summary.averageDelta !== null && Math.abs(summary.averageDelta) <= 1;

  return (
    <section className="rounded-[16px] border border-border-light bg-white p-5 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
              Local scores today
            </h3>
            <span
              className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                agrees
                  ? "bg-teal/10 text-teal"
                  : "bg-terracotta/10 text-terracotta"
              }`}
            >
              {agrees ? "On the ground agrees" : "Ground reports differ"}
            </span>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-text-muted">
            {deltaCopy(summary.averageDelta)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-right">
          <ScoreStat label="People" value={summary.averageObservedScore} />
          <ScoreStat label="PeakWave" value={summary.averagePredictedScore} />
        </div>
      </div>

      {summary.windows.length > 0 ? (
        <div className="mt-4 space-y-2">
          {summary.windows.slice(0, 5).map((window) => (
            <div key={window.id} className="rounded-sm bg-warm-bg px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[12px] font-semibold text-navy">{window.label}</p>
                <p className="text-[12px] text-text-muted">
                  {window.reportCount} review{window.reportCount !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                <span className="font-semibold text-charcoal">
                  People {formatScore(window.averageObservedScore)}
                </span>
                <span className="text-text-muted">
                  PeakWave {formatScore(window.averagePredictedScore)}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ScoreStat({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-sm bg-warm-bg px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">
        {label}
      </p>
      <p className="font-serif text-[24px] font-bold leading-tight text-navy">
        {formatScore(value)}
      </p>
    </div>
  );
}

function formatScore(score: number | null): string {
  return score === null ? "New" : `${scoreOutOf100(score)}/100`;
}

function deltaCopy(delta: number | null): string {
  if (delta === null) return "Reviews are building a local accuracy record.";
  if (Math.abs(delta) < 0.2) return "People are scoring it close to PeakWave.";
  const amount = Math.abs(delta).toFixed(1);
  return delta > 0
    ? `People are scoring it ${amount} higher than PeakWave.`
    : `People are scoring it ${amount} lower than PeakWave.`;
}

function sportName(sport: string): string {
  if (sportIsBeta(sport)) return sportLabel(sport);
  const labels: Record<string, string> = {
    "off-piste": "Off-piste",
    "downwind-foil": "Downwind foil",
    "wing-foil": "Wing foil",
    bodyboarding: "Bodyboarding",
    diving: "Diving",
    foil: "Prone foil",
    mtb: "MTB",
    piste: "Piste",
    sailing: "Sailing",
    ski: "Ski",
    sup: "SUP",
    surf: "Surf",
    swim: "Swimming",
    touring: "Touring",
    wind: "Wind",
  };
  return labels[sport] ?? sport;
}
